(() => {
  const initAccordions = (root = document) => {
    root.querySelectorAll('[data-dh-accordion][data-single-open="true"]').forEach((accordion) => {
      if (accordion.dataset.dhAccordionReady === 'true') return;
      accordion.dataset.dhAccordionReady = 'true';

      accordion.querySelectorAll('details').forEach((item) => {
        item.addEventListener('toggle', () => {
          if (!item.open) return;
          accordion.querySelectorAll('details[open]').forEach((other) => {
            if (other !== item) other.removeAttribute('open');
          });
        });
      });
    });
  };

  const initQuantities = (root = document) => {
    root.querySelectorAll('[data-dh-quantity]').forEach((quantity) => {
      if (quantity.dataset.dhQuantityReady === 'true') return;
      quantity.dataset.dhQuantityReady = 'true';
      const input = quantity.querySelector('input[name="quantity"]');
      if (!input) return;
      quantity.querySelector('[data-dh-quantity-minus]')?.addEventListener('click', () => {
        const minimum = Number(input.min || 1);
        input.value = String(Math.max(minimum, Number(input.value || minimum) - Number(input.step || 1)));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      quantity.querySelector('[data-dh-quantity-plus]')?.addEventListener('click', () => {
        const maximum = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
        input.value = String(Math.min(maximum, Number(input.value || input.min || 1) + Number(input.step || 1)));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  };

  class DhLocalQuiz extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';

      this.form = this.querySelector('[data-dh-quiz-form]');
      this.steps = [...this.querySelectorAll('[data-dh-quiz-step]')];
      this.startScreen = this.querySelector('[data-dh-quiz-start]');
      this.startButton = this.querySelector('[data-dh-quiz-start-button]');
      this.stage = this.querySelector('[data-dh-quiz-stage]');
      this.result = this.querySelector('[data-dh-quiz-result]');
      this.summary = this.querySelector('[data-dh-quiz-summary]');
      this.controls = this.querySelector('[data-dh-quiz-controls]');
      this.next = this.querySelector('[data-dh-quiz-next]');
      this.back = this.querySelector('[data-dh-quiz-back]');
      this.restart = this.querySelector('[data-dh-quiz-restart]');
      this.progress = this.querySelector('[data-dh-quiz-progress]');
      this.status = this.querySelector('[data-dh-quiz-status]');
      this.current = 0;
      this.started = false;

      if (this.dataset.fullScreen === 'true') {
        document.documentElement.classList.add('dh-full-screen-quiz-active');
      }

      if (!this.form || !this.steps.length) return;

      this.form.addEventListener('change', (event) => {
        if (!event.target.matches('input[type="radio"], input[type="checkbox"]')) return;
        if (event.target.type === 'checkbox' && event.target.checked) {
          const step = event.target.closest('[data-dh-quiz-step]');
          const choices = [...(step?.querySelectorAll('input[type="checkbox"]') || [])];
          const isExclusive = /^(none|none of the above)\b/i.test(event.target.value.trim());
          choices.forEach((choice) => {
            if (choice === event.target) return;
            const choiceIsExclusive = /^(none|none of the above)\b/i.test(choice.value.trim());
            if (isExclusive || choiceIsExclusive) choice.checked = false;
          });
        }
        this.update();
      });
      this.startButton?.addEventListener('click', () => this.startQuiz());
      this.next?.addEventListener('click', () => this.advance());
      this.back?.addEventListener('click', () => this.retreat());
      this.restart?.addEventListener('click', () => this.resetQuiz());
      this.showStart();
    }

    disconnectedCallback() {
      if (this.dataset.fullScreen !== 'true') return;
      if (!document.querySelector('dh-local-quiz[data-full-screen="true"]')) {
        document.documentElement.classList.remove('dh-full-screen-quiz-active');
      }
    }

    selectedFor(index) {
      return [...(this.steps[index]?.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked') || [])];
    }

    focusStep(index) {
      this.steps[index]?.querySelector('legend, input[type="radio"], input[type="checkbox"]')?.focus();
    }

    showStart() {
      this.started = false;
      this.current = 0;
      this.steps.forEach((step) => { step.hidden = true; });
      if (this.result) this.result.hidden = true;
      if (this.stage) this.stage.hidden = true;
      if (this.startScreen) this.startScreen.hidden = false;
      if (this.next) {
        this.next.hidden = false;
        this.next.disabled = true;
        this.next.textContent = this.dataset.nextLabel || this.next.textContent;
      }
      if (this.back) {
        this.back.hidden = false;
        this.back.disabled = true;
      }
      if (this.restart) this.restart.hidden = true;
      if (this.progress) this.progress.style.width = '0%';
      if (this.status) this.status.textContent = '';
    }

    startQuiz() {
      this.started = true;
      if (this.startScreen) this.startScreen.hidden = true;
      if (this.stage) this.stage.hidden = false;
      this.update();
      this.focusStep(0);
    }

    update() {
      if (!this.started) return;
      const showingResult = !this.result?.hidden;
      this.steps.forEach((step, index) => { step.hidden = showingResult || index !== this.current; });

      if (this.progress) {
        const ratio = showingResult ? 1 : (this.current + 1) / this.steps.length;
        this.progress.style.width = `${Math.round(ratio * 100)}%`;
      }
      if (this.status) {
        this.status.textContent = showingResult ? 'Complete' : `Question ${this.current + 1} of ${this.steps.length}`;
      }
      if (this.next) this.next.disabled = this.selectedFor(this.current).length === 0;
      if (this.next) {
        this.next.textContent = this.current === this.steps.length - 1
          ? (this.dataset.finishLabel || this.dataset.nextLabel || this.next.textContent)
          : (this.dataset.nextLabel || this.next.textContent);
      }
      if (this.back) this.back.disabled = this.current === 0;
    }

    advance() {
      if (this.selectedFor(this.current).length === 0) return;
      if (this.current < this.steps.length - 1) {
        this.current += 1;
        this.update();
        this.focusStep(this.current);
        return;
      }
      this.showResult();
    }

    retreat() {
      if (this.current === 0) return;
      this.current -= 1;
      this.update();
      this.focusStep(this.current);
    }

    showResult() {
      if (!this.result || !this.summary) return;
      this.summary.replaceChildren();
      this.steps.forEach((step, index) => {
        const selected = this.selectedFor(index);
        if (!selected.length) return;
        const item = document.createElement('li');
        item.textContent = selected.map((input) => input.value).join(', ');
        this.summary.append(item);
      });
      this.steps.forEach((step) => { step.hidden = true; });
      this.result.hidden = false;
      if (this.next) this.next.hidden = true;
      if (this.back) this.back.hidden = true;
      if (this.restart) this.restart.hidden = false;
      this.update();
      this.result.focus();
    }

    resetQuiz() {
      this.form.reset();
      this.showStart();
      this.startButton?.focus();
    }
  }

  class DhNativeCarousel extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.track = this.querySelector('[data-dh-carousel-track]');
      this.slides = [...this.querySelectorAll('[data-dh-carousel-slide]')];
      this.dots = [...this.querySelectorAll('[data-dh-carousel-dot]')];
      this.previous = this.querySelector('[data-dh-carousel-previous]');
      this.next = this.querySelector('[data-dh-carousel-next]');
      this.status = this.querySelector('[data-dh-carousel-status]');
      this.activeIndex = 0;
      this.frame = null;
      if (!this.track || this.slides.length === 0) return;

      this.previous?.addEventListener('click', () => this.goTo(this.activeIndex - 1, true));
      this.next?.addEventListener('click', () => this.goTo(this.activeIndex + 1, true));
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.goTo(Number(dot.dataset.dhCarouselDot), true)));
      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.activeIndex - 1, true);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.activeIndex + 1, true);
        } else if (event.key === 'Home') {
          event.preventDefault();
          this.goTo(0, true);
        } else if (event.key === 'End') {
          event.preventDefault();
          this.goTo(this.slides.length - 1, true);
        }
      });
      this.track.addEventListener('scroll', () => this.scheduleUpdate(), { passive: true });
      this.resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => this.scheduleUpdate());
      this.resizeObserver?.observe(this.track);
      this.updateActive(false);
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    scheduleUpdate() {
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.updateActive(false);
      });
    }

    closestIndex() {
      const padding = Number.parseFloat(getComputedStyle(this.track).paddingInlineStart) || 0;
      const leadingEdge = this.track.scrollLeft + padding;
      let closest = 0;
      let distance = Number.POSITIVE_INFINITY;
      this.slides.forEach((slide, index) => {
        const nextDistance = Math.abs(leadingEdge - slide.offsetLeft);
        if (nextDistance < distance) {
          closest = index;
          distance = nextDistance;
        }
      });
      return closest;
    }

    updateActive(announce) {
      this.setActive(this.closestIndex(), announce);
    }

    setActive(index, announce) {
      this.activeIndex = index;
      this.slides.forEach((slide, index) => {
        if (index === this.activeIndex) slide.setAttribute('aria-current', 'true');
        else slide.removeAttribute('aria-current');
      });
      this.dots.forEach((dot, index) => {
        if (index === this.activeIndex) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (this.previous) this.previous.disabled = this.activeIndex === 0;
      if (this.next) this.next.disabled = this.activeIndex === this.slides.length - 1;
      if (announce && this.status) this.status.textContent = `Slide ${this.activeIndex + 1} of ${this.slides.length}`;
    }

    goTo(index, announce = false) {
      const targetIndex = Math.max(0, Math.min(index, this.slides.length - 1));
      const slide = this.slides[targetIndex];
      const padding = Number.parseFloat(getComputedStyle(this.track).paddingInlineStart) || 0;
      const left = slide.offsetLeft - padding;
      this.activeIndex = targetIndex;
      this.track.scrollTo({ left, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      this.setActive(targetIndex, announce);
    }
  }

  class DhBlogFilter extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.form = this.querySelector('[data-dh-blog-form]');
      this.category = this.querySelector('[data-dh-blog-category]');
      this.search = this.querySelector('[data-dh-blog-search]');
      this.cards = [...this.querySelectorAll('[data-dh-blog-card]')];
      this.status = this.querySelector('[data-dh-blog-status]');
      this.empty = this.querySelector('[data-dh-blog-empty]');
      this.form?.addEventListener('submit', (event) => event.preventDefault());
      this.category?.addEventListener('change', () => this.filter());
      this.search?.addEventListener('input', () => this.filter());
      this.filter();
    }

    filter() {
      const category = (this.category?.value || '').trim().toLowerCase();
      const query = (this.search?.value || '').trim().toLowerCase();
      let visible = 0;
      this.cards.forEach((card) => {
        const matchesCategory = !category || (card.dataset.categories || '').split('|').some((tag) => tag.trim() === category);
        const matchesQuery = !query || (card.dataset.title || '').includes(query);
        const matches = matchesCategory && matchesQuery;
        card.hidden = !matches;
        card.setAttribute('aria-hidden', String(!matches));
        if (matches) visible += 1;
      });
      if (this.status) this.status.textContent = `${visible} ${visible === 1 ? 'result' : 'results'} found.`;
      if (this.empty) this.empty.hidden = visible !== 0;
    }
  }

  if (!customElements.get('dh-local-quiz')) customElements.define('dh-local-quiz', DhLocalQuiz);
  if (!customElements.get('dh-native-carousel')) customElements.define('dh-native-carousel', DhNativeCarousel);
  if (!customElements.get('dh-blog-filter')) customElements.define('dh-blog-filter', DhBlogFilter);

  const init = () => {
    initAccordions();
    initQuantities();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();

  document.addEventListener('shopify:section:load', (event) => {
    initAccordions(event.target);
    initQuantities(event.target);
  });
})();
